// Projects (BP20, Pencelj, ...): which project a spoken or typed text belongs to, from the project
// names and their aliases, and the project terms that go into the STT vocabulary.
//
// The input comes from speech recognition, so matching is forgiving: case and diacritics do not
// matter ("pencelj" == "PENCELJ" == "Pencélj"), and a name is found however the recogniser split
// it into words ("BP20" matches "BP 20", "bp-20" and "B P 20"), but only on word boundaries
// ("BP20" never matches "BP200" or "ABP20"). Declined forms ("Penclja", "na Penclju") are not
// derived: the user adds them as aliases. A name made of digits only ("2026") is never matched
// automatically (it would match quantities and dates); it still goes into the vocabulary.

export interface ProjectTerms {
  id: string;
  name: string;
  aliases: readonly string[];
  /** Archived projects are kept for old documents but never assigned automatically. */
  archived?: boolean;
}

export interface ProjectMatch {
  projectId: string;
  /** The name or alias that was found. */
  term: string;
  /** The exact substring of the input that was recognised. */
  matchedText: string;
  /** Character offset of `matchedText` in the input. */
  index: number;
}

export const MAX_PROJECT_NAME = 60;
export const MAX_PROJECT_ALIASES = 20;
export const MAX_PROJECTS = 200;

/** Lower case without diacritics ("Čez Žagar" -> "cez zagar"), for comparing spoken text. */
export function foldTerm(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{M}+/gu, '')
    .toLocaleLowerCase('sl');
}

interface Token {
  /** Folded. */
  text: string;
  /** Offset and length in the original text. */
  index: number;
  length: number;
}

const TOKEN_RE = /[\p{L}\p{N}]+/gu;

/** Letter/digit runs of the text (folded), with their places in the original. */
function tokenize(text: string): Token[] {
  const tokens: Token[] = [];
  for (const m of text.matchAll(TOKEN_RE)) tokens.push({ text: foldTerm(m[0]), index: m.index, length: m[0].length });
  return tokens;
}

/** "BP 20" -> "bp20": the letters and digits of a term, folded. */
export function termKey(term: string): string {
  return (foldTerm(term).match(TOKEN_RE) ?? []).join('');
}

/** Terms that can be matched automatically: at least one letter and two characters. */
function matchable(key: string): boolean {
  return key.length >= 2 && /\p{L}/u.test(key);
}

/**
 * The project mentioned first in `text` (the earliest match wins; at the same place the longer
 * term, then the earlier project), or null. Archived projects are skipped.
 */
export function matchProject(text: string, projects: readonly ProjectTerms[]): ProjectMatch | null {
  if (!text) return null;
  const tokens = tokenize(text);
  if (tokens.length === 0) return null;
  let best: (ProjectMatch & { key: string }) | null = null;
  for (const project of projects) {
    if (project.archived) continue;
    for (const term of [project.name, ...project.aliases]) {
      const key = termKey(term);
      if (!matchable(key)) continue;
      for (let i = 0; i < tokens.length; i++) {
        if (best && tokens[i]!.index > best.index) break;
        let joined = '';
        for (let j = i; j < tokens.length; j++) {
          joined += tokens[j]!.text;
          if (!key.startsWith(joined)) break;
          if (joined === key) {
            const start = tokens[i]!.index;
            const last = tokens[j]!;
            const end = last.index + last.length;
            if (!best || start < best.index || (start === best.index && key.length > best.key.length)) {
              best = { projectId: project.id, term, matchedText: text.slice(start, end), index: start, key };
            }
            break;
          }
        }
      }
    }
  }
  if (!best) return null;
  const { key: _key, ...match } = best;
  return match;
}

/**
 * The project names and aliases for the STT vocabulary (archived projects included: their names
 * still come up), trimmed, without duplicates of each other or of `vocabulary` (case-insensitive).
 */
export function projectVocabulary(projects: readonly ProjectTerms[], vocabulary: readonly string[] = []): string[] {
  const seen = new Set(vocabulary.map((t) => t.trim().toLocaleLowerCase('sl')));
  const out: string[] = [];
  for (const project of projects) {
    for (const raw of [project.name, ...project.aliases]) {
      const term = raw.trim().replace(/\s+/g, ' ');
      if (!term) continue;
      const key = term.toLocaleLowerCase('sl');
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(term);
    }
  }
  return out;
}
